import { RESOListing, addressToString, isDefined, orUndefined } from '@aloft/toolkit-shared';
import { useQueries } from '@tanstack/react-query';

import { useMemo } from 'react';

import { ListingResult } from '../controller/hooks/listings/types.ts';
import { useToolkitControllerGlobal } from '../controller/hooks/useToolkitControllerGlobal.ts';
import { fetchGetCoordinatesForAddressesAPI } from '../hooks/api/toolkitComponents.ts';
import { useToolkitContext } from '../hooks/api/toolkitContext.ts';
import { useMakeStreetViewStaticURL } from '../hooks/googleMaps/useMakeStreetViewStaticURL.ts';
import { DataSheet } from '../modules/referenceView/types.ts';
import { selectPropertyIds } from '../redux/features/selectors/orderSelectors.ts';
import { selectPropertyDataEntities } from '../redux/features/selectors/propertyDataSelectors/propertyDataSelectors.ts';
import { transactionToToolkitListingID } from '../redux/features/selectors/utils/transactionToToolkitListingID.ts';
import { useAppSelector } from '../redux/hooks.ts';
import { CompPropertyDataState, SubjectPropertyDataState } from '../redux/types.ts';

type PropertyImageSourceType =
  | 'userUploaded'
  | Extract<DataSheet, 'mls-images' | 'mls-street-view'>;

type PropertyImage = {
  src: string;
  type: 'url' | 'blobID';
  source: PropertyImageSourceType;
  tooltip: string;
};

const getMlsPhotoUrl = (
  listingData?: ListingResult,
  backupListing?: RESOListing,
): string | undefined => {
  const backupMedia = backupListing?.Media?.[0]?.MediaURL;
  if (!listingData || listingData.status !== 'found') return backupMedia;

  const listingMedia = listingData.listing.Media?.[0]?.MediaURL;
  if (!listingMedia) return backupMedia;

  const backupAddress = backupListing?.UnparsedAddress;
  if (!backupAddress || listingData.listing.UnparsedAddress === backupAddress) return listingMedia;

  return backupMedia;
};

const createImage = (
  src: string,
  type: 'url' | 'blobID',
  source: PropertyImageSourceType,
  tooltip: string,
): PropertyImage => ({ src, type, source, tooltip });

const getPropertyImageObject = (
  userUploaded?: string,
  mls?: string,
  streetView?: string,
): PropertyImage | undefined => {
  if (userUploaded) return createImage(userUploaded, 'blobID', 'userUploaded', 'Edit Photo');
  if (mls) return createImage(mls, 'url', 'mls-images', 'View Photos');
  if (streetView) return createImage(streetView, 'url', 'mls-street-view', 'View Street View');
  return undefined;
};

const usePropertyImages = (propertyIds: string[]): Record<string, PropertyImage> => {
  const propertyDataEntities = useAppSelector(selectPropertyDataEntities);
  const { fetcherOptions } = useToolkitContext();
  const { mlsListingsBatcher } = useToolkitControllerGlobal();
  const makeStreetViewStaticURL = useMakeStreetViewStaticURL();

  const properties = useMemo(
    () =>
      Object.entries(propertyDataEntities)
        .filter(([id, propertyDataEntity]) => propertyIds.includes(id) && propertyDataEntity)
        .map(([id, propertyDataEntity]) => {
          const propertyData =
            id === 'subject'
              ? (propertyDataEntity as SubjectPropertyDataState).subjectData
              : (propertyDataEntity as CompPropertyDataState).compData;
          const { editableData, readonlyData } = propertyData;
          const { address, coordinates, userUploadedPhotoBlobId } =
            editableData.propertyIdentification;

          return {
            id,
            listingID: transactionToToolkitListingID(editableData.transaction),
            address,
            coordinates,
            userUploadedPhotoBlobId,
            backupListing: readonlyData.backupListingData?.listing,
          };
        }),
    [propertyDataEntities, propertyIds],
  );

  const listingQueries = useQueries({
    queries: properties.map(({ listingID }) => ({
      queryKey: ['AbodeListing', listingID ?? 'missing'],
      queryFn: () =>
        listingID
          ? mlsListingsBatcher.fetch(listingID, fetcherOptions)
          : { status: 'missing' as const },
      enabled: !!listingID,
      cacheTime: Infinity,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    })),
  });

  const propertiesWithImages = useMemo(
    () =>
      properties.map((property, i) => ({
        ...property,
        mlsPhotoUrl: getMlsPhotoUrl(
          listingQueries[i]?.data as ListingResult,
          property.backupListing,
        ),
      })),
    [properties, listingQueries],
  );

  const streetViewQueries = useQueries({
    queries: propertiesWithImages.map(
      ({ id, address, coordinates, mlsPhotoUrl, userUploadedPhotoBlobId }) => {
        const addressString = orUndefined(addressToString)(address);

        const enabled =
          !userUploadedPhotoBlobId &&
          !mlsPhotoUrl &&
          !!makeStreetViewStaticURL &&
          (!!coordinates || !!addressString);

        return {
          queryKey: ['StreetView', id, addressString, coordinates],
          queryFn: async () => {
            if (!makeStreetViewStaticURL) return null;
            let coordinatesToUse = coordinates;

            if (!coordinates && addressString) {
              try {
                const res = await fetchGetCoordinatesForAddressesAPI({
                  ...fetcherOptions,
                  queryParams: { addresses: [encodeURIComponent(addressString)] },
                });

                const latLng = res[0] as { lat?: number; lng?: number } | undefined;

                if (isDefined(latLng?.lat) && isDefined(latLng?.lng)) {
                  coordinatesToUse = { latitude: latLng.lat, longitude: latLng.lng };
                }
              } catch (e) {
                console.warn('Geocoding failed', e);
              }
            }

            const url = coordinatesToUse ? await makeStreetViewStaticURL(coordinatesToUse) : null;
            return url ?? null;
          },
          enabled,
          cacheTime: Infinity,
          staleTime: Infinity,
          refetchOnWindowFocus: false,
        };
      },
    ),
  });

  return useMemo(
    () =>
      Object.fromEntries(
        propertiesWithImages
          .map(({ id, mlsPhotoUrl, userUploadedPhotoBlobId }, i) => [
            id,
            getPropertyImageObject(
              userUploadedPhotoBlobId,
              mlsPhotoUrl,
              streetViewQueries[i]?.data ?? undefined,
            ),
          ])
          .filter((item): item is [string, PropertyImage] => !!item[1]),
      ),
    [propertiesWithImages, streetViewQueries],
  );
};

export const usePropertyImage = (propertyId: string): PropertyImage | undefined => {
  const propertyIds = useMemo(() => [propertyId], [propertyId]);
  const images = usePropertyImages(propertyIds);
  return images[propertyId];
};

export const usePropertyImageEntities = (): Record<string, PropertyImage> => {
  const propertyIds = useAppSelector(selectPropertyIds);
  return usePropertyImages(propertyIds);
};
